import { Text, Box, Flex, Loader, useMantineTheme, Group } from '@mantine/core';
import { useMemo, useState, useCallback } from 'react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { StepCard } from 'src/UI/generate-flow/StepCard';
import { UploadPillButton } from '../edit/components/UploadPillButton';
import { StepProps } from '../types';
import { useAllClientDocuments } from '../hooks/useAllClientDocuments';
import { SuitabilitySelectableList } from '../edit/components/SuitabilitySelectableList';
import { getClientId } from '../edit/utils/getClientId';
import { useFilteredItems } from '../hooks/useFilteredItems';
import { useItemSelectionMapping } from '../hooks/useItemSelectionMapping';
import { useCompanyDocuments } from 'src/company/hooks/useCompanyDocuments';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { useCombinedFileUpload } from '../hooks/useCombinedFileUpload';

const DOCX_FILE_TYPE = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const PDF_FILE_TYPE = 'application/pdf';
const APPENDIX_FILE_TYPES = [DOCX_FILE_TYPE, PDF_FILE_TYPE];

type DocumentFilter = 'all' | 'data-export' | 'meeting-note' | 'user-upload' | 'company' | 'other';

const FILTER_LABELS: Record<DocumentFilter, string> = {
  all: 'All',
  'data-export': 'Data Exports',
  'meeting-note': 'Meeting Notes',
  'user-upload': 'Client Uploads',
  company: 'Appendices',
  other: 'Other',
};

const FILTER_TABS: readonly DocumentFilter[] = [
  'all',
  'data-export',
  'meeting-note',
  'user-upload',
  'company',
  'other',
] as const;

const matchesDocumentFilter = (
  documentType: string | undefined,
  filter: DocumentFilter,
): boolean => {
  if (filter === 'all') return true;
  if (filter === 'user-upload') return documentType === 'data-upload';
  if (filter === 'company') return documentType === 'company';
  if (filter === 'other') {
    return (
      documentType !== 'data-export' &&
      documentType !== 'meeting-note' &&
      documentType !== 'meeting-agenda' &&
      documentType !== 'data-upload' &&
      documentType !== 'company'
    );
  }
  return documentType === filter;
};

export function AppendicesStep({ data, setData, filter, searchBar }: StepProps) {
  const clientId = getClientId(data);
  const [documentFilter, setDocumentFilter] = useState<DocumentFilter>('all');
  const { flagEnabledCompanyDocumentsAppendices } = useFlags();

  const { documents, isLoading: isClientDocsLoading } = useAllClientDocuments(
    data.isFamily && data.family
      ? { type: 'family', familyId: data.family.id }
      : { type: 'client', clientId },
  );

  const { documents: companyDocuments, isLoading: isCompanyDocsLoading } = useCompanyDocuments();

  const isLoading = isClientDocsLoading || isCompanyDocsLoading;

  const items = useMemo(() => {
    if (isLoading) return [];

    const clientItems = documents
      .filter(doc => !!doc.file && !doc.file.isFolder)
      .map(doc => ({
        id: doc.id,
        file: doc.file!,
        title: doc.file!.fileName,
        description: `Uploaded: ${new Date(doc.file!.createdAt).toLocaleDateString()}`,
        documentType: doc.documentType,
      }));

    const companyItems = flagEnabledCompanyDocumentsAppendices
      ? companyDocuments
          .filter(doc => !doc.isArchived && APPENDIX_FILE_TYPES.includes(doc.file.fileType))
          .map(doc => ({
            id: doc.id,
            file: {
              ...doc.file,
              isFolder: false,
              isResolved: true,
              isFiled: false,
              isTemporary: false,
              createdAt: doc.createdAt,
            },
            title: doc.file.fileName,
            description: `Uploaded: ${new Date(doc.createdAt).toLocaleDateString()}`,
            documentType: 'company' as const,
          }))
      : [];

    return [...clientItems, ...companyItems];
  }, [documents, companyDocuments, isLoading, flagEnabledCompanyDocumentsAppendices]);

  const theme = useMantineTheme();
  const selectedIds = useMemo(() => data.supportDocuments.map(d => d.id), [data.supportDocuments]);

  const familyId = data.isFamily ? data.family?.id : undefined;

  const { isUploading, handleFileUpload, handleOneDriveUpload } = useCombinedFileUpload({
    clientId,
    familyId,
    onUploaded: uploadedFiles => {
      setData(prev => ({
        ...prev,
        supportDocuments: [...prev.supportDocuments, ...uploadedFiles],
      }));
    },
    onLoadingChange: isLoading => {
      setData(prev => ({ ...prev, isLoading }));
    },
  });

  const mapIdsToSelectedFiles = useItemSelectionMapping(items);

  const handleSelectionChange = useCallback(
    (ids: string[]) => {
      setData(prev => ({
        ...prev,
        supportDocuments: mapIdsToSelectedFiles(ids),
      }));
    },
    [mapIdsToSelectedFiles, setData],
  );

  const typeFilteredItems = useMemo(
    () => items.filter(item => matchesDocumentFilter(item.documentType, documentFilter)),
    [items, documentFilter],
  );
  const filteredItems = useFilteredItems(typeFilteredItems, filter);

  const hasCompanyItems = useMemo(
    () => items.some(item => item.documentType === 'company'),
    [items],
  );

  const visibleFilterTabs = useMemo(
    () => (hasCompanyItems ? FILTER_TABS : FILTER_TABS.filter(tab => tab !== 'company')),
    [hasCompanyItems],
  );

  return (
    <Flex direction="column" h="100%" mih={0}>
      <StepCard
        title={`Add appendices`}
        subTitle="Choose the document(s) you want to add as an appendix from this list, or upload a new one."
        searchBar={searchBar}
      >
        <Flex align="center" justify="space-between" w="100%" p="xs">
          <Group gap={0}>
            {visibleFilterTabs.map(filterType => (
              <Text
                key={filterType}
                size="sm"
                fw={FONT_WEIGHTS.semibold}
                c={documentFilter === filterType ? 'orange.7' : 'gray.6'}
                bg={documentFilter === filterType ? 'orange.1' : undefined}
                px="xs"
                py="xxs"
                style={{ cursor: 'pointer', borderRadius: theme.radius.md }}
                onClick={() => setDocumentFilter(filterType)}
              >
                {FILTER_LABELS[filterType]}
              </Text>
            ))}
          </Group>

          <UploadPillButton
            loading={isUploading}
            onFilesSelected={handleFileUpload}
            onOneDriveFilesSelected={handleOneDriveUpload}
            accept=".pdf,.docx"
            fileExtensions={['.pdf', '.docx']}
          />
        </Flex>
        <Box flex={1} mih={0}>
          {isLoading ? (
            <Flex justify="center" align="center" h="100%">
              <Loader data-testid="loader" />
            </Flex>
          ) : (
            <SuitabilitySelectableList
              items={filteredItems}
              selectMultiple
              selectedItemIds={selectedIds}
              setSelectedItemIds={handleSelectionChange}
              showReviewed={false}
              showViewButton={false}
              dateLayout="inline"
              highlightedElement={data.highlightedElement}
              emptyView={<Text c="gray.6">No appendices found</Text>}
            />
          )}
        </Box>
      </StepCard>
    </Flex>
  );
}
